<template>
  <dd
    @mouseenter="hoverItem"
    @mouseleave="leaveItem"
    @click.stop="selectOptionClick"
    @mousedown="selectOptionMouseDown"
    @keydown.enter="selectOptionClick"
    class="zm-select-dropdown__item"
    v-if="visible"
    :id="optionId"
    role="option"
    :aria-selected="itemSelected ? 'true' : 'false'"
    :aria-label="ariaLabel || currentLabel"
    ref="optionLi"
    :tabindex="computedDisabled ? -1 : 0"
    :class="[{
               'selected': itemSelected,
               'hover': hover,
               'is-disabled': computedDisabled || groupDisabled || limitReached
             },
             optionSize ? 'zm-select-dropdown__item--' + optionSize : '']"
  >
    <div class="zm-select-dropdown__item--content">
      <slot>
        <!--        <span ref="realLabelSpan" class="real-label-span" aria-hidden="true">{{ currentLabel }}</span>-->
        <span v-if="!widthLimit">
          {{ currentLabel }}
        </span>
        <div v-else>
          <zm-tooltip
            v-if="hasTips"
            ref="tooltips"
            :open-delay="0"
            :modifiers="{flip: true}"
            popper-class="zm-select-dropdown__item__tooltips"
            placement="right"
          >
            <div slot="content" class="zm-select-dropdown__item__tooltips__content" v-text="currentLabel"></div>
            <span>
              <span class="zm-select-dropdown__label_span" :style="{width: itemWidth + 'px'}">{{ currentLabel }}</span>
            </span>
          </zm-tooltip>
          <span v-else class="zm-select-dropdown__label_span">{{ currentLabel }}</span>
        </div>
      </slot>
    </div>
    <template v-if="tooltipContent && tooltipContent.length">
      <zm-tooltip placement="top">
        <div slot="content" class="zm-select-dropdown__item__tooltips__content" v-text="tooltipContent"></div>
        <span class="zm-select-dropdown__item--tooltips zm-icon-help-outline"></span>
      </zm-tooltip>
    </template>
  </dd>
</template>

<script type="text/babel">
  import ZmTooltip from '../tooltip';
  import Emitter from '../../mixins/emitter';
  import { getValueByPath, escapeRegexpString } from '../../utils/util';

  export default {
    mixins: [Emitter],

    components: {
      ZmTooltip
    },

    name: 'ZmOption',

    componentName: 'ZmOption',

    inject: ['select'],

    props: {
      value: [String, Number, Object, Boolean],
      label: [String, Number],
      ariaLabel: String,
      created: Boolean,
      disabled: {
        type: Boolean,
        default: false
      },
      tooltipContent: String
    },

    data() {
      return {
        groupDisabled: false,
        hover: false,
        visible: true,
        hitState: false,
        hasTips: false,
        itemWidth: '100%',
        widthLimit: this.select.widthLimit
      };
    },

    computed: {
      optionId() {
        return 'select-item-' + this.selectId + '-' + this.itemIndex;
      },
      isObject() {
        return Object.prototype.toString.call(this.value).toLowerCase() === '[object object]';
      },
      currentLabel() {
        return this.label || (this.isObject ? '' : this.value);
      },
      currentValue() {
        return this.value || this.label || '';
      },
      itemSelected() {
        if (!this.select.multiple) {
          return this.isEqual(this.value, this.select.value);
        } else {
          return this.contains(this.select.value, this.value);
        }
      },
      limitReached() {
        if (this.select.multiple) {
          return !this.itemSelected &&
            (this.select.value || []).length >= this.select.multipleLimit &&
            this.select.multipleLimit > 0;
        } else {
          return false;
        }
      },
      selectId() {
        return this.select.targetId;
      },
      itemIndex() {
        return this.select.options.indexOf(this);
      },
      multiSelect() {
        return this.select.multiple && !this.filterable;
      },
      filterable() {
        return this.select.$options.name === 'FilterableSelect';
      },
      optionSize() {
        return this.select.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
      },
      computedDisabled() {
        if (this.select.multiple && (this.select.remote || this.filterable)) {
          return this.disabled || this.itemSelected;
        } else {
          return this.disabled;
        }
      },
      componentName() {
        return this.filterable ? 'filterable-select' : 'normal-select';
      }
    },

    watch: {
      currentLabel() {
        if (!this.created && !this.select.remote) this.dispatch(this.componentName, 'setSelected');
      },
      value(val, oldVal) {
        const { remote, valueKey } = this.select;
        if (!this.created && !remote) {
          if (valueKey && typeof val === 'object' && typeof oldVal === 'object' && val[valueKey] === oldVal[valueKey]) {
            return;
          }
          this.dispatch(this.componentName, 'setSelected');
        }
      }
    },

    methods: {
      isEqual(a, b) {
        if (!this.isObject) {
          return a === b;
        } else {
          const valueKey = this.select.valueKey;
          return getValueByPath(a, valueKey) === getValueByPath(b, valueKey);
        }
      },

      contains(arr = [], target) {
        if (!this.isObject) {
          return arr.indexOf(target) > -1;
        } else {
          const valueKey = this.select.valueKey;
          return arr.some(item => {
            return getValueByPath(item, valueKey) === getValueByPath(target, valueKey);
          });
        }
      },

      handleGroupDisabled(val) {
        this.groupDisabled = val;
      },
      checkShowTips() {
        if (!this.widthLimit) {
          return;
        }
        const div = document.createElement('div');
        div.setAttribute('class', 'zm-temp-real-label-span');
        div.innerText = this.currentLabel;
        document.body.appendChild(div);
        const iWidth = div.getBoundingClientRect().width;

        if (iWidth + 24 > this.itemWidth) {
          this.hasTips = true;
        } else {
          this.hasTips = false;
        }
        this.$nextTick(() => {
          document.body.removeChild(div);
        });
      },
      hoverItem() {
        if (!this.computedDisabled && !this.groupDisabled) {
          this.select.hoverIndex = this.select.options.indexOf(this);
        }
      },
      leaveItem() {
        this.hover = false;
      },

      selectOptionClick() {
        if (this.multiSelect) {
          return;
        }
        if (this.computedDisabled !== true && this.groupDisabled !== true) {
          this.dispatch(this.componentName, 'handleOptionClick', [this, true]);
        }
      },

      selectOptionMouseDown() {
        this.dispatch(this.componentName, 'handleOptionMouseDown');
      },

      queryChange(query) {
        this.visible = new RegExp(escapeRegexpString(query), 'i').test(this.currentLabel) || this.created;
        if (!this.visible) {
          this.select.filteredOptionsCount--;
        }
      }
    },
    mounted() {
      if (this.widthLimit && this.select && this.select.$refs && this.select.$refs.popper) {
        this.itemWidth = parseInt(this.select.$refs.popper.minWidth, 10) - 24;
        this.checkShowTips();
      }
    },
    created() {
      this.select.options.push(this);
      this.select.cachedOptions.push(this);
      this.select.optionsCount++;
      if (this.filterable) {
        this.select.filteredOptionsCount++;
      }

      this.$on('queryChange', this.queryChange);
      this.$on('handleGroupDisabled', this.handleGroupDisabled);
    },
    beforeDestroy() {
      const index = this.select.options.indexOf(this);
      const cachedIndex = this.select.cachedOptions.indexOf(this);
      // fix memory leak
      if (index === -1 && cachedIndex > -1) {
        this.select.cachedOptions.splice(cachedIndex, 1);
      }
      if (index > -1) {
        this.select.optionsCount--;
        if (this.filterable) {
          this.filteredOptionsCount--;
        }
        this.select.options.splice(index, 1);
      }
    }
  };
</script>

<style lang="scss">
  @import "./option";
</style>
